import React, { useCallback, useEffect, useState } from "react";
import DataSetupMainCard from "./DataSetupMainCard";
import DataSetupDrawer from "./DataSetupDrawer";
import { MuiTable, TextValidator, Label, Autocomplete } from "components";
import {
  IconButton,
  Tooltip,
  Box,
  Grid,
  useTheme,
  Chip,
  Dialog,
  Grow,
  Stack,
  Typography,
  CircularProgress,
  Switch,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import StatusConfirmDialog from "components/StatusConfirmDialog";
import SubCategoryService from "services/SubCategoryService";
import CategoryService from "services/CategoryService";
import FileUpload from "components/FileUpload";
import ImageLoader from "components/ImageLoader";

const SubCategorySetupCard = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [data, setData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    search: null,
  });

  const [drawer, setDrawer] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    displayOrder: "",
    categoryId: null,
    image: [],
    currentImage: "",
  });

  // Status update state
  const [statusDialog, setStatusDialog] = useState({
    open: false,
    loading: false,
    subCategoryId: null,
    subCategoryName: "",
    newStatus: null,
  });

  // View modal state
  const [viewModal, setViewModal] = useState({
    open: false,
    item: null,
  });

  const setPage = (page) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      page: page,
    }));
  };

  const setPageSize = (pageSize) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      page: 1,
      limit: pageSize,
    }));
  };

  const getData = useCallback(async () => {
    setLoading(true);
    const response = await SubCategoryService.getAll({
      ...filters,
      page: filters.page - 1,
    });
    if (response.success) {
      setData(response?.data?.view?.data || []);
      setTotalItems(response?.data?.view?.totalItems || 0);
    }
    setLoading(false);
  }, [filters]);

  useEffect(() => {
    getData();
    fetchCategories();
  }, [getData]);

  const fetchCategories = async () => {
    try {
      const response = await CategoryService.getAll({
        page: 0,
        limit: 100,
        isActive: true,
      });
      if (response.success) {
        setCategories(response?.data?.view?.data || []);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (files) => {
    setFormData((prev) => ({
      ...prev,
      image: files,
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const formPayload = new FormData();
      formPayload.append("name", formData.name);
      formPayload.append("description", formData.description);
      formPayload.append("displayOrder", formData.displayOrder);
      formPayload.append(
        "categoryId",
        formData.categoryId?.id || formData.categoryId,
      );
      if (formData.image.length > 0) {
        formPayload.append("image", formData.image[0]);
      }

      if (isEdit) {
        await SubCategoryService.update(editingId, formPayload);
      } else {
        await SubCategoryService.create(formPayload);
      }

      setFormData({
        name: "",
        description: "",
        displayOrder: "",
        categoryId: null,
        image: [],
        currentImage: "",
      });
      setEditingId(null);
      setDrawer(false);
      getData();
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const handleEditClick = (item) => {
    setEditingId(item.id);
    setFormData({
      name: item.name || "",
      description: item.description || "",
      displayOrder: item.displayOrder || "",
      categoryId: categories.find((cat) => cat.id === item.categoryId) || null,
      image: [],
      currentImage: item.image || "",
    });
    setIsEdit(true);
    setDrawer(true);
  };

  const handleAddClick = () => {
    setEditingId(null);
    setFormData({
      name: "",
      description: "",
      displayOrder: "",
      categoryId: null,
      image: [],
      currentImage: "",
    });
    setIsEdit(false);
    setDrawer(true);
  };

  /**
   * Handle view button click
   * Opens modal to display subcategory details
   */
  const handleViewClick = (item) => {
    setViewModal({
      open: true,
      item: item,
    });
  };

  /**
   * Handle status toggle button click
   * Opens confirmation dialog before updating status
   */
  const handleStatusToggle = (item) => {
    const newStatus = !item.isActive;
    setStatusDialog({
      open: true,
      loading: false,
      subCategoryId: item.id,
      subCategoryName: item.name,
      newStatus: newStatus,
    });
  };

  /**
   * Confirm status change and call API
   */
  const handleStatusConfirm = async () => {
    const { subCategoryId, newStatus } = statusDialog;

    try {
      setStatusDialog((prev) => ({ ...prev, loading: true }));

      const payload = {
        isActive: newStatus,
        companyId: filters.companyId,
      };

      const response = await SubCategoryService.updateStatus(
        subCategoryId,
        payload,
      );

      if (response.success) {
        // Refresh data to get updated status
        await getData();
        console.log("✅ Sub Category status updated successfully");
      } else {
        console.error(
          "❌ Failed to update sub category status:",
          response.error,
        );
      }

      setStatusDialog((prev) => ({ ...prev, open: false, loading: false }));
    } catch (error) {
      console.error("Error updating status:", error);
      setStatusDialog((prev) => ({ ...prev, open: false, loading: false }));
    }
  };

  return (
    <DataSetupMainCard
      iconSrc="/assets/images/icons/subcategory.png"
      title="Sub Category"
      subtitle="Manage your sub categories"
      actionIconSrc="/assets/images/icons/plus.png"
      actionText="Add New Sub Category"
      onAddClick={handleAddClick}
    >
      <MuiTable
        columns={[
          {
            accessorKey: "actions",
            header: "Actions",
            Cell: ({ cell }) => {
              const item = cell.row.original;
              const isUpdating =
                statusDialog.subCategoryId === item.id && statusDialog.loading;

              return (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <Tooltip title="View">
                    <IconButton
                      size="small"
                      color="info"
                      onClick={() => handleViewClick(item)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEditClick(item)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={item.isActive ? "Deactivate" : "Activate"}>
                    <Box sx={{ position: "relative" }}>
                      <Switch
                        checked={item.isActive}
                        size="small"
                        disabled={isUpdating}
                        onChange={() => handleStatusToggle(item)}
                      />
                      {isUpdating && (
                        <CircularProgress
                          size={20}
                          sx={{
                            position: "absolute",
                            left: "50%",
                            top: "50%",
                            transform: "translate(-50%, -50%)",
                            opacity: 0.7,
                          }}
                        />
                      )}
                    </Box>
                  </Tooltip>
                </Box>
              );
            },
          },
          {
            accessorKey: "name",
            header: "Name",
          },
          {
            accessorKey: "Category.name",
            header: "Category",
          },
          {
            accessorKey: "description",
            header: "Description",
          },
          {
            accessorKey: "displayOrder",
            header: "Display Order",
          },
          {
            accessorKey: "image",
            header: "Image",
            Cell: ({ cell }) => (
              <ImageLoader
                imagePath={cell.getValue()}
                width={80}
                height={80}
                alt={cell.row.original.name}
                rounded={true}
                borderRadius={1}
              />
            ),
          },
          {
            accessorKey: "isActive",
            header: "Status",
            Cell: ({ cell }) => {
              const isActive = cell.getValue();

              return (
                <Chip
                  label={isActive ? "Active" : "Inactive"}
                  variant="filled"
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    letterSpacing: 0.3,
                    background: isActive
                      ? `linear-gradient(135deg, ${theme.palette.success.main}15 0%, ${theme.palette.success.light}15 100%)`
                      : `linear-gradient(135deg, ${theme.palette.warning.main}15 0%, ${theme.palette.warning.light}15 100%)`,
                    color: isActive
                      ? theme.palette.success.dark
                      : theme.palette.warning.dark,
                    border: `2px solid ${
                      isActive
                        ? theme.palette.success.main
                        : theme.palette.warning.main
                    }`,
                    borderRadius: "20px",
                    padding: "4px 8px",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: isActive
                        ? `0 4px 12px ${theme.palette.success.main}40`
                        : `0 4px 12px ${theme.palette.warning.main}40`,
                    },
                  }}
                />
              );
            },
          },
        ]}
        data={data}
        title=""
        manualPagination={true}
        rowCount={totalItems}
        currentPage={filters.page}
        pageSize={filters.limit}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        loading={loading}
      />

      <DataSetupDrawer
        maxWidth="md"
        open={drawer}
        onClose={() => setDrawer(false)}
        iconSrc="/assets/images/icons/subcategory.png"
        title={isEdit ? "Edit Sub Category" : "Add Sub Category"}
        subtitle="Create and manage sub category details"
        submitLabel={isEdit ? "Update" : "Create"}
        onSubmit={handleFormSubmit}
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box gap={1} display="flex" flexDirection="column">
              {/* Sub Category Name */}
              <TextValidator
                label="Sub Category Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                validators={["required", "minStringLength:2"]}
                errorMessages={[
                  "Sub category name is required",
                  "Sub category name must be at least 2 characters",
                ]}
                fullWidth
                size="small"
                placeholder="Enter sub category name"
              />

              {/* Category ID */}
              <Autocomplete
                name="categoryId"
                label="Category"
                value={formData.categoryId}
                onChange={(event, newValue) => {
                  setFormData((prev) => ({
                    ...prev,
                    categoryId: newValue,
                  }));
                }}
                options={categories}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id
                }
                validators={["required"]}
                errorMessages={["Category is required"]}
                fullWidth
                size="small"
                placeholder="Select category"
              />

              {/* Description */}
              <TextValidator
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                validators={["required"]}
                errorMessages={["Description is required"]}
                fullWidth
                size="small"
                multiline
                rows={3}
                placeholder="Describe the sub category"
              />

              {/* Display Order */}
              <TextValidator
                label="Display Order"
                name="displayOrder"
                value={formData.displayOrder}
                onChange={handleInputChange}
                validators={["required", "isNumber"]}
                errorMessages={[
                  "Display order is required",
                  "Display order must be a number",
                ]}
                fullWidth
                size="small"
                type="number"
                placeholder="Enter display order"
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            {/* Image Upload */}
            <Box>
              <FileUpload
                value={formData.image}
                onChange={handleImageChange}
                multiple={false}
                required={false}
                acceptedFileTypes={["image/jpeg", "image/png", "image/webp"]}
                maxSingleFileSize={5 * 1024 * 1024}
                label={
                  formData.image.length > 0
                    ? "Change Image"
                    : "Upload Sub Category Image"
                }
                helperText="Accepted formats: JPG, PNG, WEBP (Max 5MB)"
              />
            </Box>
          </Grid>
          <Grid size={12}>
            {isEdit && formData.currentImage && (
              <Box
                sx={{
                  mb: 2,
                  borderRadius: 2,
                }}
              >
                <Label>Current Image Preview</Label>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "start",
                    borderRadius: 1,
                    overflow: "hidden",
                    mt: 1,
                  }}
                >
                  <ImageLoader
                    imagePath={formData.currentImage}
                    width={80}
                    height={80}
                    alt="Current sub category image"
                    rounded={true}
                    borderRadius={1}
                  />
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>
        <></>
      </DataSetupDrawer>

      {/* Status Confirmation Dialog */}
      <StatusConfirmDialog
        open={statusDialog.open}
        onClose={() => setStatusDialog((prev) => ({ ...prev, open: false }))}
        onConfirm={handleStatusConfirm}
        title={
          statusDialog.newStatus
            ? "Activate Sub Category"
            : "Deactivate Sub Category"
        }
        message={
          statusDialog.newStatus
            ? "Are you sure you want to activate this sub category? It will be visible to customers."
            : "Are you sure you want to deactivate this sub category? It will no longer be visible to customers."
        }
        itemName={statusDialog.subCategoryName}
        newStatus={statusDialog.newStatus}
        loading={statusDialog.loading}
      />

      {/* View Sub Category Modal */}
      <Dialog
        open={viewModal.open}
        onClose={() => setViewModal({ open: false, item: null })}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Grow}
        transitionDuration={{
          enter: 400,
          exit: 300,
        }}
        PaperProps={{
          sx: {
            display: "flex",
            flexDirection: "column",
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            maxHeight: "90vh",
            boxShadow: `0 20px 60px rgba(0, 0, 0, 0.15)`,
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
            transition: "all 0.4s ease-in-out",
          },
        }}
      >
        {/* ===== Header with Icon ===== */}
        <Box
          sx={{
            p: 3,
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* Icon Container */}
          <Box
            component="img"
            src="/assets/images/icons/subcategory.png"
            alt="Sub Category Icon"
            sx={{
              width: 48,
              height: 48,
              minWidth: 48,
              objectFit: "contain",
              filter:
                "brightness(0) saturate(100%) invert(43%) sepia(99%) saturate(1235%) hue-rotate(244deg)",
              opacity: 0.8,
            }}
          />

          {/* Title and Subtitle */}
          <Box sx={{ flexGrow: 1, pr: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 0.5,
                letterSpacing: 0.2,
              }}
            >
              Sub Category Details
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.875rem",
              }}
            >
              View sub category information
            </Typography>
          </Box>

          {/* Close Button */}
          <IconButton
            onClick={() => setViewModal({ open: false, item: null })}
            sx={{
              mt: -0.5,
              color: theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: `${theme.palette.primary.main}20`,
                color: theme.palette.primary.main,
              },
              transition: "all 0.2s ease",
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* ===== Content ===== */}
        <Box
          sx={{
            p: 3,
            flexGrow: 1,
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: 6,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.background.default,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.divider,
              borderRadius: 3,
              "&:hover": {
                backgroundColor: theme.palette.text.disabled,
              },
            },
          }}
        >
          {viewModal.item && (
            <Stack spacing={3}>
              {/* Sub Category Image */}
              {viewModal.item.image && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mb: 1,
                  }}
                >
                  <ImageLoader
                    imagePath={viewModal.item.image}
                    width={150}
                    height={150}
                    alt={viewModal.item.name}
                    rounded={true}
                    borderRadius={2}
                  />
                </Box>
              )}

              {/* Sub Category Name */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {viewModal.item.name}
                </Typography>
              </Box>

              {/* Category */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Category
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {viewModal.item.Category?.name}
                </Typography>
              </Box>

              {/* Description */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Description
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.6,
                  }}
                >
                  {viewModal.item.description}
                </Typography>
              </Box>

              {/* Display Order */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Display Order
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {viewModal.item.displayOrder}
                </Typography>
              </Box>

              {/* Status */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Status
                </Typography>
                <Chip
                  label={viewModal.item.isActive ? "Active" : "Inactive"}
                  variant="filled"
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    letterSpacing: 0.3,
                    background: viewModal.item.isActive
                      ? `linear-gradient(135deg, ${theme.palette.success.main}15 0%, ${theme.palette.success.light}15 100%)`
                      : `linear-gradient(135deg, ${theme.palette.warning.main}15 0%, ${theme.palette.warning.light}15 100%)`,
                    color: viewModal.item.isActive
                      ? theme.palette.success.dark
                      : theme.palette.warning.dark,
                    border: `2px solid ${
                      viewModal.item.isActive
                        ? theme.palette.success.main
                        : theme.palette.warning.main
                    }`,
                    borderRadius: "20px",
                    padding: "4px 8px",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: viewModal.item.isActive
                        ? `0 4px 12px ${theme.palette.success.main}40`
                        : `0 4px 12px ${theme.palette.warning.main}40`,
                    },
                  }}
                />
              </Box>
            </Stack>
          )}
        </Box>
      </Dialog>
    </DataSetupMainCard>
  );
};

export default SubCategorySetupCard;
